import {
  type AuthResponse,
  AuthResponseSchema,
  type AuthenticatedCharacter,
  type ClassId,
  HttpRefusalSchema,
  type Look,
} from '@shefira/shared';

import { serverText } from '../i18n/server-text.js';
import { uiFormat } from '../ui/text.js';

/**
 * Guest sign-up and the token that outlives a page reload.
 *
 * localStorage holds the session token and nothing else (CLAUDE.md): no game
 * state, no position. Everything the client believes about the character comes
 * back from the server on the next join.
 */

const STORAGE_KEY = 'shefira.session';

export interface StoredSession {
  token: string;
  expiresAt: number;
  character: AuthenticatedCharacter;
}

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = AuthResponseSchema.safeParse(JSON.parse(raw));
    // A token past its expiry is worse than none: it fails the join with an
    // error the player cannot act on, so drop it and ask for a nickname again.
    if (!parsed.success || parsed.data.expiresAt <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function saveSession(session: AuthResponse): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // A private window can refuse storage; the session still works for this tab.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clean up */
  }
}

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * The server's refusal of an HTTP call, its line already in the player's
 * language (ADR 0069): the server writes French, with the template of a line
 * that carries values.
 */
export function refusalError(body: unknown, fallback: string, status: number): AuthError {
  const parsed = HttpRefusalSchema.safeParse(body);
  const refusal = parsed.success ? parsed.data : {};
  const line = refusal.details?.[0];
  const text =
    line === undefined
      ? uiFormat(fallback, {})
      : serverText({
          text: line,
          ...(refusal.key !== undefined && { key: refusal.key }),
          ...(refusal.params !== undefined && { params: refusal.params }),
        });
  return new AuthError(text, refusal.error ?? String(status));
}

export function serverHttpUrl(): string {
  const configured = import.meta.env.VITE_SERVER_URL;
  if (configured !== undefined) return configured;
  // In production Caddy serves the client and proxies the API from one origin
  // (infra/Caddyfile), so the build needs no server address baked in.
  return import.meta.env.DEV ? 'http://localhost:2567' : window.location.origin;
}

export function serverWsUrl(): string {
  return serverHttpUrl().replace(/^http/, 'ws');
}

/** Create a guest account with a chosen class and look. */
export async function registerGuest(
  nickname: string,
  classId: ClassId,
  look: Look,
): Promise<AuthResponse> {
  let response: Response;
  try {
    response = await fetch(`${serverHttpUrl()}/auth/guest`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ nickname, classId, look }),
    });
  } catch {
    throw new AuthError('Serveur injoignable. Il tourne bien ?', 'unreachable');
  }

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw refusalError(body, 'Connexion refusée', response.status);
  }

  const parsed = AuthResponseSchema.safeParse(body);
  if (!parsed.success) throw new AuthError('Réponse du serveur inattendue', 'bad_response');
  saveSession(parsed.data);
  return parsed.data;
}
